/* ==========================================================================
   Optimize Minerals — fit the mega menu products to their box

   The product shots are square PNGs with the product floating in the middle,
   and how much empty space is baked in varies by asset: the Supplements set is
   trimmed to within a few percent, the Bundles set carries 18-34% on every
   side. Dropped into the comp's 100x68 box that padding becomes dead space, so
   the gap to the name under it read as 25-30px in one panel and 12px in the
   other - from the same 12px rule - and no two products in a row sat on the
   same line or at a comparable size.

   CSS cannot see it. object-fit: contain honours the whole file, whitespace
   included, and a blanket crop deep enough to fix the Bundles panel would cut
   into the Supplements one. The padding is a property of each image, so it has
   to be measured from each image.

   So: find where the artwork actually ends on all four sides, then place the
   picture so that rectangle - not the file - is what fits the box, sitting on
   its bottom edge and flush with its left. The result is the comp's row: every
   product the same visual weight, on one baseline, 12px above its name.

   Failure is silent and total: a cross-origin refusal, a canvas the browser
   will not let us read, an image that never loads, all leave the picture
   exactly where CSS put it. Nothing here is required for the menu to work.
   ========================================================================== */

(function () {
  'use strict';

  var SCAN = 200;   /* the bounding box does not need the full-size file */

  /* The artwork's bounding box, as fractions of the file. Transparent counts as
     empty, and so does near-white: some of these were exported on the page's
     own off-white rather than on nothing, and a white rectangle is just as much
     dead space as an empty one. */
  function bounds(img) {
    var w = SCAN;
    var h = Math.max(1, Math.round(img.naturalHeight * w / img.naturalWidth));

    var c = document.createElement('canvas');
    c.width = w;
    c.height = h;
    c.getContext('2d').drawImage(img, 0, 0, w, h);

    var d;
    try {
      d = c.getContext('2d').getImageData(0, 0, w, h).data;
    } catch (e) {
      return null;  /* tainted - the CDN did not send us a usable header */
    }

    var minX = w, maxX = -1, minY = h, maxY = -1;
    for (var y = 0; y < h; y++) {
      for (var x = 0; x < w; x++) {
        var i = (y * w + x) * 4;
        if (d[i + 3] > 16 && !(d[i] > 244 && d[i + 1] > 244 && d[i + 2] > 238)) {
          if (x < minX) minX = x;
          if (x > maxX) maxX = x;
          if (y < minY) minY = y;
          if (y > maxY) maxY = y;
        }
      }
    }
    if (maxX < 0) return null;  /* an all-empty image, leave it alone */

    return {
      left: minX / w,
      top: minY / h,
      width: (maxX - minX + 1) / w,
      height: (maxY - minY + 1) / h
    };
  }

  /* ratio is the FILE's height over its width, and it has to come from the
     probe. The image on the page is lazy and lives inside a panel that is
     hidden until the menu is opened, so at the moment this runs it has usually
     loaded nothing and reports naturalWidth 0. Dividing by that gave NaN, the
     browser dropped the `top` it produced as invalid, and every picture stayed
     where the flex box had centred it - which is the gap this file exists to
     remove, still there, on a page that had run the measurement correctly. */
  function place(img, box, b, ratio) {
    /* The CONTENT box. clientWidth includes padding, and the drawer's tiles are
       a 56px square with 6px of it kept clear on purpose - measured off
       clientWidth the artwork would be fitted to the tile's outer edge and the
       inset would be gone. The mega menu's boxes have no padding, so this
       subtracts nothing there. */
    var cs = getComputedStyle(box);
    var padX = parseFloat(cs.paddingLeft) + parseFloat(cs.paddingRight);
    var padY = parseFloat(cs.paddingTop) + parseFloat(cs.paddingBottom);
    var bw = box.clientWidth - padX;
    var bh = box.clientHeight - padY;
    if (!(bw > 0) || !(bh > 0)) return;

    /* Scale the ARTWORK to fit the box, then work back to the size the whole
       file has to be drawn at for its artwork to come out that size. */
    var scale = Math.min(bw / b.width, bh / b.height);
    var fileW = scale;
    var fileH = scale * ratio;
    if (!isFinite(fileW) || !isFinite(fileH)) return;

    box.style.position = 'relative';

    /* Every one of these has to be written with `important`. The rules in
       op-chrome.css that size the picture are themselves !important - they have
       to be, the markup carries Tailwind utilities that would otherwise win -
       and an !important declaration outranks a plain inline style. Setting
       .style.width here looked like it worked, the attribute was on the
       element, and the browser went on drawing the CSS size: the picture stayed
       68px wide while being positioned as though it were 124, which put some of
       them above the top of their box where overflow:hidden swallowed them
       whole. The CSS sizing stays as the no-JS fallback; this replaces it. */
    set(img, 'position', 'absolute');
    set(img, 'width', fileW.toFixed(2) + 'px');
    set(img, 'height', 'auto');
    set(img, 'max-width', 'none');
    set(img, 'max-height', 'none');
    /* Where the trimmed artwork sits in the box it was just fitted to.

       The mega menu is a ROW: four products side by side, and what makes a row
       read as a row is a shared baseline and a shared left edge. So there the
       artwork is flush left and sitting on the bottom.

       The drawer's shelf is not a row, it is a list - one product per line in
       its own 56px square tile, with nothing beside it to line up against. The
       same rule there just pushed each picture into the bottom-left corner of
       its own tile, which reads as a mistake rather than as alignment: the
       dropper, being tall and narrow, ended up 12px outside its box on the
       left. A tile with one thing in it wants that thing in the middle.

       Only one axis actually has slack to distribute - `scale` fits the other
       exactly - so centring is a no-op on whichever axis was the tight one. */
    var centred = box.classList.contains('op-drawer-shop__media');

    var artW = b.width * fileW;
    var artH = b.height * fileH;

    var left = centred
      ? parseFloat(cs.paddingLeft) + (bw - artW) / 2 - b.left * fileW
      : parseFloat(cs.paddingLeft) - b.left * fileW;

    var top = centred
      ? parseFloat(cs.paddingTop) + (bh - artH) / 2 - b.top * fileH
      : parseFloat(cs.paddingTop) + bh - (b.top + b.height) * fileH;

    set(img, 'left', left.toFixed(2) + 'px');
    set(img, 'top', top.toFixed(2) + 'px');
  }

  function set(el, prop, value) {
    el.style.setProperty(prop, value, 'important');
  }

  /* Measuring and placing are separate because they become ready at different
     moments. The measurement only needs the file; the placement needs the box
     to have a size, and the mobile drawer is display:none until it is opened -
     every tile in it is 0x0 at load, so a placement attempted then computes a
     negative content box and gives up. The mega menu got away with it only
     because its panels are hidden with visibility, which still lays them out.

     So the bounds are cached on the element and a ResizeObserver replays the
     placement the moment the tile is first given a size. It also covers the
     rest of it for free: an orientation change, the drawer being reopened at a
     different width, a theme-editor re-render. */
  var sized = new ResizeObserver(function (entries) {
    entries.forEach(function (entry) {
      var box = entry.target;
      var img = box.querySelector('img');
      if (img && img.__opBounds) place(img, box, img.__opBounds, img.__opRatio);
    });
  });

  /* A copy, because the canvas needs an image the CDN has let us read. The one
     on the page was fetched without crossorigin and cannot be put on a canvas,
     even though the bytes are identical and already in cache. */
  function measure(img) {
    var src = img.currentSrc || img.src;
    var box = img.parentElement;
    if (!src || !box) return;

    var probe = new Image();
    probe.crossOrigin = 'anonymous';
    probe.onload = function () {
      if (!probe.naturalWidth) return;
      var b;
      try {
        b = bounds(probe);
      } catch (e) {
        return;
      }
      if (!b) return;

      img.__opBounds = b;
      img.__opRatio = probe.naturalHeight / probe.naturalWidth;
      place(img, box, b, img.__opRatio);
      sized.observe(box);
    };
    probe.src = src;
  }

  function run() {
    /* The drawer's shelf carries the same assets into a 56px tile, where the
       padding costs even more: half of a 44px picture is a lot of nothing. */
    var sel = '.mega-menu__content .header-product-image img,' +
              '.mega-menu__content .promo-product-image img,' +
              '.op-drawer-shop__media img';
    document.querySelectorAll(sel).forEach(function (img) {
      if (img.dataset.opTrimmed === '1') return;
      img.dataset.opTrimmed = '1';
      measure(img);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }
  /* The header re-renders when it is edited in the theme editor. */
  document.addEventListener('shopify:section:load', run);
})();
